import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import {
  getAdminMe,
  getStats,
  getRevenueOverview,
  getPendingApprovals,
  
} from '../api';

import AdminShell from './AdminShell';
import '../styles/AdminDashboard.css';
import '../styles/GlassAdmin.css';

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip
);

function formatCurrency(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(Number(value));
}

function formatNumber(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }

  return new Intl.NumberFormat('en-IN').format(Number(value));
}

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState({ labels: [], data: [] });
  const [approvals, setApprovals] = useState([]);

  const [loading, setLoading] = useState(true);
  const [approvalsLoading, setApprovalsLoading] = useState(true);
  

  const loadApprovals = async () => {
    setApprovalsLoading(true);

    try {
      const { data } = await getPendingApprovals();
      setApprovals(data.approvals || []);
    } catch (err) {
      console.error('Failed to load pending approvals:', err);
      setApprovals([]);
    } finally {
      setApprovalsLoading(false);
    }
  };

  const loadDashboard = async () => {
    setLoading(true);

    try {
      const [statsResponse, revenueResponse] = await Promise.all([
        getStats(),
        getRevenueOverview()
      ]);

      setStats(statsResponse.data.stats || null);
      setRevenue(revenueResponse.data.revenue || { labels: [], data: [] });
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAdminMe()
      .then(({ data }) => setAdmin(data.admin))
      .catch(() => {
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        navigate('/login');
      });

    loadDashboard();
    loadApprovals();
  }, [navigate]);

  const refreshDashboard = async () => {
    await Promise.all([
      loadDashboard(),
      loadApprovals()
    ]);
  };



  const hasRevenueData =
    Array.isArray(revenue.labels) &&
    revenue.labels.length > 0 &&
    Array.isArray(revenue.data) &&
    revenue.data.length > 0;

  const chartData = {
    labels: hasRevenueData ? revenue.labels : [],
    datasets: [
      {
        label: 'Revenue',
        data: hasRevenueData ? revenue.data : [],
        borderColor: '#041627',
        backgroundColor: (ctx) => {
          const chart = ctx.chart;

          if (!chart.chartArea) {
            return 'rgba(210, 228, 251, 0.25)';
          }

          const gradient = chart.ctx.createLinearGradient(
            0,
            chart.chartArea.top,
            0,
            chart.chartArea.bottom
          );

          gradient.addColorStop(0, 'rgba(210, 228, 251, 0.5)');
          gradient.addColorStop(1, 'rgba(210, 228, 251, 0)');

          return gradient;
        },
        borderWidth: 2,
        pointBackgroundColor: '#ffffff',
        pointBorderColor: '#041627',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: true,
        tension: 0.4
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: '#0b1c30',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        padding: 12,
        displayColors: false,
        callbacks: {
          label: (context) => formatCurrency(context.parsed.y)
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#74777d',
          font: {
            family: 'Inter',
            size: 12
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#c4c6cd',
          borderDash: [5, 5]
        },
        ticks: {
          color: '#74777d',
          font: {
            family: 'Inter',
            size: 12
          },
          callback: (value) => {
            const numericValue = Number(value);

            if (numericValue >= 1000) {
              return `₹${(numericValue / 1000).toFixed(
                numericValue % 1000 === 0 ? 0 : 1
              )}k`;
            }

            return `₹${numericValue}`;
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  return (
    <AdminShell
      admin={admin}
      badges={{ pendingApprovals: stats?.pendingApprovals }}
      pageTitle="Dashboard Overview"
    >
      <div className="glass-page">
        <div className="dash-body">

          {/* Welcome */}
          <div className="dash-welcome-row">
            <div>
              <h2 className="dash-welcome-title">
                Welcome back
                {admin ? `, ${admin.name.split(' ')[0]}` : ''}
              </h2>

              <p className="dash-welcome-subtitle">
                Here's what's happening on Tedo Bizz today.
              </p>
            </div>

            <div className="dash-quick-actions">
              <button
                className="dash-quick-btn primary"
                onClick={() => navigate('/categories')}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 18 }}
                >
                  add
                </span>
                Add New Category
              </button>

              <button
                className="dash-quick-btn secondary"
                onClick={() => navigate('/vendors')}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 18 }}
                >
                  storefront
                </span>
                Manage Vendors
              </button>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="glass-kpi-grid">

            <div className="glass-kpi-card">
              <div className="glass-kpi-top">
                <span className="material-symbols-outlined glass-kpi-icon">
                  storefront
                </span>
              </div>

              <div className="glass-kpi-value">
                {loading ? '…' : formatNumber(stats?.totalVendors)}
              </div>

              <div className="glass-kpi-sub">
                Total Vendors
              </div>
            </div>

            <div className="glass-kpi-card">
              <div className="glass-kpi-top">
                <span className="material-symbols-outlined glass-kpi-icon">
                  check_circle
                </span>
              </div>

              <div className="glass-kpi-value">
                {loading ? '…' : formatNumber(stats?.activeSubscriptions)}
              </div>

              <div className="glass-kpi-sub">
                Active Subscriptions
              </div>
            </div>

            <div className="glass-kpi-card">
              <div className="glass-kpi-top">
                <span className="material-symbols-outlined glass-kpi-icon">
                  payments
                </span>
              </div>

              <div className="glass-kpi-value">
                {loading ? '…' : formatCurrency(stats?.monthlyRevenue)}
              </div>

              <div className="glass-kpi-sub">
                Current Subscription Revenue
              </div>
            </div>

            <div className="glass-kpi-card">
              <div className="glass-kpi-top">
                <span className="material-symbols-outlined glass-kpi-icon">
                  warning
                </span>

                {stats?.pendingApprovals > 0 && (
                  <span className="glass-kpi-tag urgent">
                    Action Required
                  </span>
                )}
              </div>

              <div className="glass-kpi-value">
                {loading ? '…' : formatNumber(stats?.pendingApprovals)}
              </div>

              <div className="glass-kpi-sub">
                Pending Approvals
              </div>
            </div>

          </div>

          {/* Revenue + Approvals */}
          <div className="dash-grid-row">

            {/* Revenue */}
            <div className="glass-panel dash-panel-chart">
              <div className="dash-panel-header">
                <div>
                  <h3 className="dash-panel-title">
                    Revenue Overview
                  </h3>

                  <p className="glass-kpi-sub" style={{ margin: '5px 0 0' }}>
                    Subscription revenue — last 6 months
                  </p>
                </div>
              </div>

              <div className="dash-chart-wrapper">
                {loading ? (
                  <div className="dash-empty-state">
                    Loading revenue data…
                  </div>
                ) : !hasRevenueData ? (
                  <div className="dash-empty-state">
                    No revenue data available yet.
                  </div>
                ) : (
                  <Line
                    data={chartData}
                    options={chartOptions}
                  />
                )}
              </div>
            </div>

            {/* Pending Approvals */}
            <div className="glass-panel dash-panel">

              <div className="dash-panel-table-header">
                <div>
                  <h3
                    className="dash-panel-title"
                    style={{ fontSize: 20 }}
                  >
                    Pending Approvals
                  </h3>

                  <p
                    className="glass-kpi-sub"
                    style={{ margin: '4px 0 0' }}
                  >
                    Vendors waiting for verification
                  </p>
                </div>

                <button
                  className="dash-view-all-link"
                  onClick={() => navigate('/approvals')}
                  style={{
                    background: 'none',
                    border: 'none'
                  }}
                >
                  View All
                </button>
              </div>

              <div className="dash-table-scroll">

                {approvalsLoading ? (
                  <div className="dash-empty-state">
                    Loading approvals…
                  </div>
                ) : approvals.length === 0 ? (
                  <div className="dash-empty-state">
                    <span
                      className="material-symbols-outlined"
                      style={{
                        fontSize: 32,
                        display: 'block',
                        marginBottom: 8
                      }}
                    >
                      task_alt
                    </span>

                    No pending approvals.
                  </div>
                ) : (
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th className="right">Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {approvals.map((approval) => {
                        

                        return (
                          <tr key={approval.id}>
                            <td>
                              <div className="glass-vendor-cell">

                                <div className="glass-avatar-tile">
                                  {approval.name?.[0]?.toUpperCase() || '?'}
                                </div>

                                <div>
                                  <p className="dash-vendor-name">
                                    {approval.name}
                                  </p>

                                  <p className="dash-vendor-meta">
                                    {approval.category || 'Uncategorized'}
                                    {' • '}
                                    {approval.date || '—'}
                                  </p>
                                </div>

                              </div>
                            </td>

                            <td className="dash-table-actions">

                              <button
  className="glass-icon-btn"
  title="Review approval"
  onClick={() => navigate('/approvals')}
>
  <span
    className="material-symbols-outlined"
    style={{ fontSize: 18 }}
  >
    open_in_new
  </span>
</button>

                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

              </div>
            </div>

          </div>

        </div>
      </div>
    </AdminShell>
  );
}